import { Field, Select } from '../FormShell';
import {
  ROI_LINE_KINDS,
  ROI_LINE_KIND_LABELS,
  ROI_PERIODS,
  ROI_PERIOD_LABELS,
  type RoiLineKind,
  type RoiPeriod,
} from '../../lib/types';
import { breakdownTotals, buildCost, isComputedLabor, lineAmount, netFirstYear, paybackMonths } from '../../lib/roi';
import { formatMoney } from '../../lib/format';
import { lineFromDraft, newDraftLine, type RoiLineDraft } from './roiDraft';

const kindOptions = ROI_LINE_KINDS.map((k) => ({ value: k, label: ROI_LINE_KIND_LABELS[k] }));
const periodOptions = ROI_PERIODS.map((p) => ({ value: p, label: ROI_PERIOD_LABELS[p] }));

const smallButton = 'px-2 py-1 rounded border border-slate-300 text-slate-700 hover:bg-slate-100 text-sm';

interface RoiCalculatorProps {
  open: boolean;
  onToggle: () => void;
  lines: RoiLineDraft[];
  onChange: (lines: RoiLineDraft[]) => void;
  /** Writes the totals (whole dollars) into the ROI one-time / per-year fields. */
  onUseTotals: (oneTime: number, annual: number) => void;
  /** Current cost-to-build inputs (blank = null), for the cost / net / payback lines. */
  tokensCost: number | null;
  additionalCost: number | null;
  /** The ROI fields as currently typed (what gets saved); net and payback are based on these, not the lines. */
  roiOneTime: number | null;
  roiAnnual: number | null;
}

/** Collapsible line-item calculator under the ROI fields. */
export function RoiCalculator({
  open, onToggle, lines, onChange, onUseTotals, tokensCost, additionalCost, roiOneTime, roiAnnual,
}: RoiCalculatorProps) {
  const totals = breakdownTotals(lines.map(lineFromDraft));
  const cost = buildCost({ tokensCost, additionalCost });
  const net = netFirstYear(roiOneTime ?? 0, roiAnnual ?? 0, cost);
  const payback = paybackMonths(cost, roiAnnual ?? 0);

  const update = (key: number, patch: Partial<RoiLineDraft>) =>
    onChange(lines.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const removeLine = (key: number) => onChange(lines.filter((l) => l.key !== key));

  return (
    <div className="border border-slate-200 rounded">
      <button
        type="button"
        id="roi-calculator-toggle"
        aria-expanded={open}
        aria-controls="roi-calculator"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-atg-blue hover:bg-slate-50 rounded"
      >
        <span>ROI calculator{lines.length ? ` (${lines.length} ${lines.length === 1 ? 'line' : 'lines'})` : ''}</span>
        <span aria-hidden="true">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div id="roi-calculator" className="px-3 pb-3 flex flex-col gap-3">
          {lines.length === 0 && (
            <p className="text-sm text-slate-500">
              Add a line for each saving: labor hours, an avoided hire, a contract you can drop.
            </p>
          )}
          {lines.map((line, i) => {
            const prefix = `roiBreakdown[${i}]`;
            const idPrefix = `roi-line-${i}`;
            const computed = isComputedLabor(lineFromDraft(line));
            return (
              <div key={line.key} className="border border-slate-200 rounded p-2 flex flex-col gap-2 bg-slate-50">
                <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)_auto] gap-2 items-end">
                  <Select label="Kind" name={`${prefix}.kind`} id={`${idPrefix}-kind`} value={line.kind} options={kindOptions}
                    onChange={(e) => update(line.key, { kind: e.target.value as RoiLineKind })} />
                  <Field label="Label (optional)" name={`${prefix}.label`} id={`${idPrefix}-label`} maxLength={100}
                    placeholder="e.g. 2 FTE on invoices" value={line.label}
                    onChange={(e) => update(line.key, { label: e.target.value })} />
                  {computed ? (
                    <Field label="Amount ($)" name={`${prefix}.amount`} id={`${idPrefix}-amount`} readOnly
                      aria-readonly="true" className="bg-slate-100"
                      value={formatMoney(lineAmount(lineFromDraft(line)))} />
                  ) : (
                    <Field label="Amount ($)" name={`${prefix}.amount`} id={`${idPrefix}-amount`} type="number" min={0} step={1}
                      value={line.amount} onChange={(e) => update(line.key, { amount: e.target.value })} />
                  )}
                  <Select label="Period" name={`${prefix}.period`} id={`${idPrefix}-period`}
                    value={computed ? 'PerYear' : line.period} disabled={computed} options={periodOptions}
                    onChange={(e) => update(line.key, { period: e.target.value as RoiPeriod })} />
                  <button type="button" onClick={() => removeLine(line.key)} id={`${idPrefix}-remove`}
                    aria-label={`Remove line ${i + 1}`}
                    className="mb-0.5 px-2 py-2 rounded text-slate-500 hover:text-red-700 hover:bg-red-50 leading-none">
                    ×
                  </button>
                </div>
                {line.kind === 'Labor' && (
                  <div className="grid grid-cols-[1fr_1fr_2fr] gap-2 items-end">
                    <Field label="Hours/week" name={`${prefix}.hoursPerWeek`} id={`${idPrefix}-hours`} type="number" min={0} step="any"
                      value={line.hoursPerWeek} onChange={(e) => update(line.key, { hoursPerWeek: e.target.value })} />
                    <Field label="$/hour" name={`${prefix}.hourlyRate`} id={`${idPrefix}-rate`} type="number" min={0} step="any"
                      value={line.hourlyRate} onChange={(e) => update(line.key, { hourlyRate: e.target.value })} />
                    <p className="text-xs text-slate-500 pb-2">
                      {computed ? 'Amount = hours × rate × 52, per year.' : 'Fill both to compute the yearly amount.'}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
          <div>
            <button type="button" id="roi-add-line" className={smallButton} onClick={() => onChange([...lines, newDraftLine()])}>
              + Add line
            </button>
          </div>
          <div className="flex flex-col gap-1 border-t border-slate-200 pt-2 text-sm text-slate-700" aria-live="polite">
            <div className="flex items-center justify-between gap-3">
              <span id="roi-calculator-totals">
                One-time: <strong>{formatMoney(totals.oneTime)}</strong> · Per year: <strong>{formatMoney(totals.annual)}</strong>
              </span>
              <button type="button" id="roi-use-totals" className={smallButton} disabled={lines.length === 0}
                onClick={() => onUseTotals(Math.round(totals.oneTime), Math.round(totals.annual))}>
                Use totals
              </button>
            </div>
            <span id="roi-calculator-cost">
              Cost to build: <strong>{formatMoney(cost)}</strong>
            </span>
            <span id="roi-calculator-net">
              Net first year: <strong className={net < 0 ? 'text-red-700' : undefined}>{formatMoney(net)}</strong>
              {payback !== null && (
                <> · Payback: <strong>{payback} {payback === 1 ? 'month' : 'months'}</strong></>
              )}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
